import { Controller } from 'react-hook-form';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../api/vault-api.ts';
import VaultMatterHeader from './shared/vault-matter-header.tsx';
import DecomNotifyUsersInput from './shared/decom-notify-users-input.tsx';
import DecomReportRecipientInput from './shared/decom-report-recipient-input.tsx';
import VaultTooltipWithLink from './shared/vault-tooltip-with-link.tsx';
import VaultExportConfigSkeleton from './shared/vault-export-config-skeleton.tsx';
import VaultTimezoneSelect from './shared/vault-timezone-select.tsx';
import VaultDateRangePicker from './shared/vault-date-range-picker.tsx';
import { DestUser, VAULT_DATA_SCOPE_OPTIONS } from '../../../../../../types.ts';

import { ActionErrorEntry } from '../decom-policy-utils.tsx';

import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

export interface VaultGroupsExportConfigProps {
  matter?: VaultMatterModel;
  isLoading?: boolean;
  day: number;
  idx: number;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
}

export default function VaultGroupsExportConfigOld({
  matter,
  isLoading,
  day,
  idx,
  watch,
  setValue,
  control,
  errors,
  clearErrors,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
}: VaultGroupsExportConfigProps) {
  const handleBack = () => {
    setValue(`actions.${day}.actions.${idx}.matterId`, '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  if (isLoading) return <VaultExportConfigSkeleton onBack={handleBack} />;

  if (!matter) return null;

  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      <VaultMatterHeader matterName={matter.name} onBack={handleBack} />

      <Stack direction="row" gap={4}>
        <Controller
          name={`actions.${day}.actions.${idx}.dataScope`}
          control={control}
          defaultValue="ALL_DATA"
          render={({ field }) => (
            <FormControl sx={{ minWidth: 350 }} size="small">
              <Typography variant="subtitle2" gutterBottom>
                Source
              </Typography>
              <Select {...field} value={field.value || ''}>
                {VAULT_DATA_SCOPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Stack>

      <Stack direction="row" gap={4}>
        <VaultTimezoneSelect day={day} idx={idx} control={control} />

        <VaultDateRangePicker
          day={day}
          idx={idx}
          control={control}
          errors={errors}
          label="Date Sent (optional)"
          tooltipText="Only emails sent within this date range will be exported."
        />
      </Stack>

      <Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle2" gutterBottom>
            Terms (optional)
          </Typography>
          <VaultTooltipWithLink
            preset="groupsQueryTerms"
            sxIcon={{ mb: 0.5 }}
          />
        </Stack>
        <Controller
          name={`actions.${day}.actions.${idx}.terms`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value || ''}
              placeholder="Query Terms"
              helperText="Example: from username1 subject: Hello has:attachment"
              size="small"
              fullWidth
              variant="standard"
            />
          )}
        />
      </Box>

      <Divider sx={{ my: 1 }} />

      <DecomNotifyUsersInput
        day={day}
        idx={idx}
        control={control}
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
      />

      <DecomReportRecipientInput
        day={day}
        idx={idx}
        control={control}
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        actionType="vaultGroupsExport"
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={destUserFields}
        destUserAppend={destUserAppend}
        destUserRemove={destUserRemove}
        destUserUpdate={destUserUpdate}
        titleLabel="Set Destination User"
        customLabel="Custom User:"
        autocompleteLabel="Search for a user"
      />
    </Stack>
  );
}
